/**
 * Browser-side language detection and speech synthesis.
 *
 * Both are first-class paths, not consolation prizes:
 *
 *  - `SpeechRecognition` gives free, instant, real-time partial transcription on
 *    Chrome, Edge and Safari. On the Free plan this is the difference between a
 *    working live mode and an exhausted budget.
 *  - `speechSynthesis` speaks forty-plus languages on-device with zero cost and
 *    no network round trip, which makes it the default TTS.
 *
 * Both degrade honestly: `available` is false when the API is missing, and the
 * UI switches to the server path rather than pretending.
 */

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

interface SpeechRecognitionAlternative {
  readonly transcript: string;
  readonly confidence: number;
}

interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionResultList {
  readonly length: number;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionEventLike extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEventLike extends Event {
  readonly error: string;
  readonly message?: string;
}

export interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
  onspeechend: (() => void) | null;
}

const getRecognitionCtor = (): SpeechRecognitionCtor | null => {
  const scope = globalThis as unknown as Record<string, unknown>;
  return (scope["SpeechRecognition"] ?? scope["webkitSpeechRecognition"]) as SpeechRecognitionCtor | null;
};

export function isBrowserAsrAvailable(): boolean {
  return getRecognitionCtor() !== null;
}

export type LocalAsrHandler = (event: { text: string; final: boolean; confidence: number; lang: string }) => void;

/**
 * Wraps the Web Speech recognizer.
 *
 * `continuous` is forced on because the whole product is a long conversation,
 * and Chrome's implementation ends the session after a pause otherwise. The
 * auto-restart in `onend` is what actually makes it usable: without it, a
 * three-second silence ends the translation.
 */
export class LocalRecognizer {
  #recognition: SpeechRecognitionLike | null = null;
  #handler: LocalAsrHandler;
  #running = false;
  #wanted = false;
  #lang: string;
  #restartTimer: ReturnType<typeof setTimeout> | null = null;
  #onRunningChange: ((running: boolean) => void) | null = null;

  constructor(handler: LocalAsrHandler, lang: string, onRunningChange?: (running: boolean) => void) {
    this.#handler = handler;
    this.#lang = lang;
    this.#onRunningChange = onRunningChange ?? null;
  }

  get isRunning(): boolean {
    return this.#running;
  }

  setLanguage(locale: string): void {
    this.#lang = locale;
    if (this.#recognition) this.#recognition.lang = locale;
  }

  start(): boolean {
    if (this.#running) return true;
    const Ctor = getRecognitionCtor();
    if (!Ctor) return false;

    this.#wanted = true;
    this.#spawn(Ctor);
    return true;
  }

  stop(): void {
    this.#wanted = false;
    if (this.#restartTimer !== null) {
      clearTimeout(this.#restartTimer);
      this.#restartTimer = null;
    }
    this.#recognition?.stop();
  }

  #spawn(Ctor: SpeechRecognitionCtor): void {
    const recognition = new Ctor();
    recognition.lang = this.#lang;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const alternative = result?.[0];
        if (!alternative) continue;
        if (result.isFinal) {
          this.#handler({ text: alternative.transcript.trim(), final: true, confidence: alternative.confidence, lang: this.#lang });
        } else {
          interim += alternative.transcript;
        }
      }
      if (interim.trim() !== "") {
        this.#handler({ text: interim.trim(), final: false, confidence: 0, lang: this.#lang });
      }
    };

    recognition.onerror = (event) => {
      // `no-speech` and `aborted` are normal, not failures.
      if (event.error === "no-speech" || event.error === "aborted") return;
      this.#handler({ text: "", final: true, confidence: 0, lang: this.#lang });
    };

    recognition.onend = () => {
      this.#setRunning(false);
      this.#recognition = null;
      if (!this.#wanted) return;
      // Chrome ends the session on every pause. Restarting is the whole trick.
      this.#restartTimer = setTimeout(() => {
        this.#restartTimer = null;
        if (!this.#wanted) return;
        this.#spawn(Ctor);
      }, 250);
    };

    recognition.onstart = () => {
      this.#setRunning(true);
    };

    this.#recognition = recognition;
    try {
      recognition.start();
    } catch {
      // `start()` throws if the previous session has not fully released.
      this.#setRunning(false);
    }
  }

  #setRunning(running: boolean): void {
    if (this.#running === running) return;
    this.#running = running;
    this.#onRunningChange?.(running);
  }
}

// ---------------------------------------------------------------------------
//  Speech synthesis
// ---------------------------------------------------------------------------

export function isBrowserTtsAvailable(): boolean {
  const scope = globalThis as unknown as Record<string, unknown>;
  return "speechSynthesis" in scope && "SpeechSynthesisUtterance" in scope;
}

export class LocalSpeaker {
  #queue: SpeechSynthesisUtterance[] = [];
  #speaking = false;
  #muted = false;
  #onStateChange: ((speaking: boolean) => void) | null = null;

  setMuted(muted: boolean): void {
    this.#muted = muted;
    if (muted) this.cancel();
  }

  set onStateChange(handler: ((speaking: boolean) => void) | null) {
    this.#onStateChange = handler;
  }

  /**
   * Queues an utterance. Returns false when the browser has no voice for it.
   *
   * The queue is serial: each `onend` releases the speaking flag *before*
   * dequeuing the next utterance. The previous version never reset the flag,
   * so exactly one utterance ever played and every later translation was
   * silently swallowed — the "voice doesn't work after the first line" bug.
   */
  speak(text: string, locale: string, options: { rate?: number; pitch?: number; onEnd?: () => void } = {}): boolean {
    if (this.#muted || text.trim() === "" || !isBrowserTtsAvailable()) return false;
    const synth = speechSynthesis;

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = locale;
    utterance.rate = options.rate ?? 1.02;
    utterance.pitch = options.pitch ?? 1;
    utterance.volume = 1;

    const voice = pickVoice(synth.getVoices(), locale);
    if (voice) utterance.voice = voice;
    utterance.onend = () => {
      options.onEnd?.();
      this.#speaking = false;
      this.#next();
    };
    utterance.onerror = () => {
      options.onEnd?.();
      this.#speaking = false;
      this.#next();
    };

    this.#queue.push(utterance);
    this.#next();
    return true;
  }

  cancel(): void {
    this.#queue = [];
    if (isBrowserTtsAvailable()) speechSynthesis.cancel();
    if (this.#speaking) {
      this.#speaking = false;
      this.#onStateChange?.(false);
    }
  }

  get pending(): number {
    return this.#queue.length;
  }

  #next(): void {
    if (this.#speaking) return;
    const next = this.#queue.shift();
    if (!next) {
      this.#onStateChange?.(false);
      return;
    }
    this.#speaking = true;
    this.#onStateChange?.(true);
    try {
      speechSynthesis.speak(next);
    } catch {
      // A synchronous throw (detached context, shutting-down page) must not
      // wedge the queue: release and move on.
      this.#speaking = false;
      this.#next();
    }
  }
}

/**
 * Voice selection.
 *
 * Exact locale first, then the base language, and only a local voice if one
 * exists — a remote voice is a network round trip plus a privacy question, so
 * it is never chosen automatically.
 */
function pickVoice(voices: readonly SpeechSynthesisVoice[], locale: string): SpeechSynthesisVoice | null {
  if (voices.length === 0) return null;
  const base = locale.split("-")[0]?.toLowerCase() ?? locale.toLowerCase();
  const local = voices.filter((voice) => voice.localService);
  const pool = local.length > 0 ? local : voices;

  return (
    pool.find((voice) => voice.lang.toLowerCase() === locale.toLowerCase()) ??
    pool.find((voice) => voice.lang.toLowerCase().split("-")[0] === base) ??
    null
  );
}

/**
 * Voice lists populate asynchronously in most browsers, and the first read is
 * usually empty. Resolves once the list is actually populated.
 */
export function whenVoicesReady(timeoutMs = 2_000): Promise<readonly SpeechSynthesisVoice[]> {
  if (!isBrowserTtsAvailable()) return Promise.resolve([]);
  const immediate = speechSynthesis.getVoices();
  if (immediate.length > 0) return Promise.resolve(immediate);

  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(speechSynthesis.getVoices()), timeoutMs);
    const handler = (): void => {
      const voices = speechSynthesis.getVoices();
      if (voices.length === 0) return;
      clearTimeout(timer);
      speechSynthesis.removeEventListener("voiceschanged", handler);
      resolve(voices);
    };
    speechSynthesis.addEventListener("voiceschanged", handler);
  });
}
